import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Sparkles, ArrowLeft, Building2, Boxes, Gauge, Network, CheckCircle2, Lightbulb,
  Pencil, Trash2, Plus, Download, Upload, Loader2,
} from 'lucide-react';
import api from '../../lib/api';

const EDITABLE = {
  property: [['name', 'Name'], ['address', 'Address'], ['city', 'City'], ['state', 'State'], ['zip', 'Zip'], ['property_type', 'Property type']],
  vendor_account: [['vendor_name', 'Vendor'], ['account_number', 'Account no.'], ['commodity', 'Commodity'], ['site_name', 'Property/Site']],
  unit_account: [['account_number', 'Account no.'], ['vendor_name', 'Vendor'], ['commodity', 'Commodity'], ['service_address', 'Service address'], ['meter_numbers', 'Meters (comma-separated)']],
  meter: [['meter_number', 'Meter no.'], ['account_number', 'Account no.'], ['vendor_name', 'Vendor']],
};
const TYPE_OF_TAB = { properties: 'property', vendor_accounts: 'vendor_account', unit_accounts: 'unit_account', meters: 'meter' };

const StatusBadge = ({ it }) => (
  <div className="flex items-center gap-1">
    {it._uploaded && <Badge className="rounded-none bg-blue-600 text-[9px]">UPLOADED</Badge>}
    {it.edited && !it._uploaded && <Badge className="rounded-none bg-violet-600 text-[9px]">EDITED</Badge>}
    {it.status === 'new'
      ? <Badge className="rounded-none bg-emerald-600 text-[10px]">NEW</Badge>
      : <Badge variant="outline" className="rounded-none text-[10px] text-muted-foreground">EXISTS</Badge>}
  </div>
);

const CountChip = ({ label, n, total }) => (
  <div className="flex flex-col px-4 py-2 bg-muted/50">
    <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
    <span className="text-lg font-bold tabular-nums">{n} <span className="text-sm font-normal text-muted-foreground">new / {total}</span></span>
  </div>
);

export default function PaasOnboard() {
  const navigate = useNavigate();
  const [currentAccount] = useState(() => {
    try { return JSON.parse(localStorage.getItem('current_account') || 'null'); } catch { return null; }
  });
  const accountId = currentAccount?.id;
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [approving, setApproving] = useState(false);
  const [result, setResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null); // {item_type, item, isNew}
  const [form, setForm] = useState({});
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await api.post(`/paas/onboard/propose?account_id=${accountId}`, { use_ai: false });
      setPlan(res.data);
    } catch { toast.error('Failed to build onboarding proposal'); }
    finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await api.post(`/paas/onboard/propose?account_id=${accountId}`, { use_ai: true });
      setPlan((p) => ({ ...(p || {}), ai_merges: res.data.ai_merges || [] }));
      toast.success(`${(res.data.ai_merges || []).length} AI merge suggestion(s)`);
    } catch { toast.error('AI suggestions failed'); }
    finally { setAiLoading(false); }
  };

  const toggle = (key) => setSelected((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const allNewItems = () => {
    if (!plan) return [];
    return ['properties', 'vendor_accounts', 'unit_accounts', 'meters'].flatMap((k) => (plan[k] || []).filter((i) => i.status === 'new'));
  };

  const approve = async (onlySelected) => {
    if (!plan) return;
    setApproving(true);
    try {
      const pick = (arr) => (arr || []).filter((i) => i.status === 'new' && (!onlySelected || selected.has(i.key)));
      const payload = { plan: {
        properties: pick(plan.properties), vendor_accounts: pick(plan.vendor_accounts),
        unit_accounts: pick(plan.unit_accounts), meters: pick(plan.meters),
      } };
      const total = payload.plan.properties.length + payload.plan.vendor_accounts.length + payload.plan.unit_accounts.length;
      if (total === 0) { toast.error('Nothing selected to onboard'); setApproving(false); return; }
      const res = await api.post(`/paas/onboard/approve?account_id=${accountId}`, payload);
      setResult(res.data);
      toast.success(`Onboarded ${res.data.created.locations} properties, ${res.data.created.vendor_accounts} vendor accounts, ${res.data.created.unit_accounts} unit accounts`);
      setSelected(new Set());
      await load();
    } catch { toast.error('Approve failed'); }
    finally { setApproving(false); }
  };

  const rollback = async () => {
    if (!result?.batch_id) return;
    try {
      await api.post(`/paas/onboard/rollback?account_id=${accountId}`, { batch_id: result.batch_id });
      toast.success('Rolled back — records re-surfaced for review');
      setResult(null); await load();
    } catch { toast.error('Rollback failed'); }
  };

  // ── edit / add drawer ──
  const openEdit = (item_type, item) => {
    setEditing({ item_type, item, isNew: false });
    const f = {}; EDITABLE[item_type].forEach(([k]) => { f[k] = Array.isArray(item[k]) ? item[k].join(', ') : (item[k] ?? ''); });
    setForm(f);
  };
  const openAdd = (item_type) => {
    setEditing({ item_type, item: null, isNew: true });
    const f = {}; EDITABLE[item_type].forEach(([k]) => { f[k] = ''; }); setForm(f);
  };
  const saveEdit = async () => {
    const { item_type, item, isNew } = editing;
    const fields = { ...form };
    if ('meter_numbers' in fields) fields.meter_numbers = String(fields.meter_numbers || '').split(',').map((s) => s.trim()).filter(Boolean);
    try {
      if (isNew) await api.post(`/paas/onboard/item?account_id=${accountId}`, { item_type, fields });
      else await api.patch(`/paas/onboard/item?account_id=${accountId}`, { item_type, key: item.key, fields });
      toast.success(isNew ? 'Row added' : 'Saved');
      setEditing(null); await load();
    } catch { toast.error('Save failed'); }
  };
  const removeRow = async (item_type, key) => {
    try {
      await api.delete(`/paas/onboard/item?account_id=${accountId}&item_type=${item_type}&key=${encodeURIComponent(key)}`);
      toast.success('Removed from review'); await load();
    } catch { toast.error('Remove failed'); }
  };

  const download = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/paas/onboard/export?account_id=${accountId}`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement('a'); a.href = URL.createObjectURL(b);
        a.download = 'onboarding_review.xlsx'; a.click(); URL.revokeObjectURL(a.href);
      }).catch(() => toast.error('Download failed'));
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`/paas/onboard/import?account_id=${accountId}&use_ai=true`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.ok) toast.success(`Imported (${res.data.source}): ${res.data.added} added, ${res.data.edited} updated`);
      else toast.error(`Could not parse file (${res.data.reason})`);
      await load();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const c = plan?.counts || {};
  const renderRow = (tab, it, subtitle) => {
    const type = TYPE_OF_TAB[tab];
    return (
      <div key={it.key} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`row-${tab}-${it.key}`}>
        {it.status === 'new' && (
          <Checkbox checked={selected.has(it.key)} onCheckedChange={() => toggle(it.key)} data-testid={`select-${it.key}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{it.name || it.vendor_name || `Meter ${it.meter_number}` || it.account_number || '—'}</div>
          <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
        </div>
        <StatusBadge it={it} />
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-none" onClick={() => openEdit(type, it)} data-testid={`edit-${it.key}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-none text-red-600" onClick={() => removeRow(type, it.key)} data-testid={`delete-${it.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="paas-onboard">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Button variant="ghost" size="sm" className="rounded-none -ml-2" onClick={() => navigate('/paas/accounts')} data-testid="onboard-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Accounts
            </Button>
            <div className="flex items-center gap-2 mt-1">
              <Sparkles className="h-6 w-6 text-amber-600" />
              <h1 className="text-2xl font-bold">UBM Onboarding Proposal</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Edit, add, upload & approve. Showing only <b>new</b> records not yet in UBM. {plan?.draft_meta?.updated_at ? `· edits saved` : ''}
            </p>
          </div>
          {!result && (
            <div className="flex items-center gap-2 flex-wrap">
              <input type="file" ref={fileRef} className="hidden" accept=".xlsx,.xls,.csv" onChange={onUpload} data-testid="onboard-file-input" />
              <Button variant="outline" className="rounded-none" onClick={download} data-testid="onboard-download"><Download className="h-4 w-4 mr-2" /> Download</Button>
              <Button variant="outline" className="rounded-none" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="onboard-upload">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload (AI)
              </Button>
              <Button variant="outline" className="rounded-none" onClick={runAi} disabled={loading || aiLoading || !plan} data-testid="onboard-ai">
                <Lightbulb className={`h-4 w-4 mr-2 ${aiLoading ? 'animate-pulse' : ''}`} /> {aiLoading ? 'Thinking…' : 'Suggest merges'}
              </Button>
              <Button variant="outline" className="rounded-none" disabled={approving || selected.size === 0} onClick={() => approve(true)} data-testid="onboard-approve-selected">
                Approve selected ({selected.size})
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="rounded-none bg-amber-600 hover:bg-amber-700" disabled={loading || approving || !plan} data-testid="onboard-approve">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Approve all
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Create all new UBM records?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Creates {c.properties_new} properties, {c.vendor_accounts_new} vendor accounts and {c.unit_accounts_new} unit accounts. Approved rows move to UBM Onboarding and leave this page. Reversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                    <AlertDialogAction className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={() => approve(false)} data-testid="onboard-approve-confirm">Approve all</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>

        {result && (
          <Card className="p-5 rounded-none border-l-4 border-l-emerald-600" data-testid="onboard-result">
            <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /><h3 className="font-semibold">Onboarding complete</h3></div>
            <p className="text-sm text-muted-foreground">Batch <code className="text-foreground">{result.batch_id}</code> · created {result.created.locations} properties, {result.created.vendor_accounts} vendor accounts, {result.created.unit_accounts} unit accounts. They now live in UBM Onboarding.</p>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" className="rounded-none" onClick={() => navigate('/ubm/onboarding')} data-testid="onboard-view-ubm">View in UBM</Button>
              <Button variant="ghost" className="rounded-none text-red-600" onClick={rollback} data-testid="onboard-rollback">Rollback this batch</Button>
              <Button variant="ghost" className="rounded-none" onClick={() => setResult(null)}>Continue onboarding</Button>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : !plan ? null : (
          <>
            <Card className="p-4 rounded-none">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <CountChip label="Properties" n={c.properties_new} total={c.properties_total} />
                <CountChip label="Vendor Accounts" n={c.vendor_accounts_new} total={c.vendor_accounts_total} />
                <CountChip label="Unit Accounts" n={c.unit_accounts_new} total={c.unit_accounts_total} />
                <CountChip label="Meters" n={c.meters_new} total={c.meters_total} />
              </div>
            </Card>

            {(plan.ai_merges || []).length > 0 && (
              <Card className="p-4 rounded-none border-l-4 border-l-blue-600" data-testid="ai-merges">
                <div className="flex items-center gap-2 mb-2"><Lightbulb className="h-4 w-4 text-blue-600" /><h3 className="text-sm font-semibold uppercase tracking-wide">AI merge suggestions</h3></div>
                {plan.ai_merges.map((m, i) => (
                  <div key={i} className="text-sm py-1 border-t border-border/50" data-testid={`ai-merge-${i}`}>
                    <span className="font-medium">{m.canonical_name}</span>
                    <span className="text-muted-foreground"> ← {(m.member_keys || []).join(', ')}</span>
                    <div className="text-xs text-muted-foreground">{m.reason}</div>
                  </div>
                ))}
              </Card>
            )}

            {(c.properties_new + c.vendor_accounts_new + c.unit_accounts_new + c.meters_new) === 0 ? (
              <Card className="p-10 rounded-none text-center" data-testid="onboard-empty">
                <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                <p className="font-medium">All caught up</p>
                <p className="text-sm text-muted-foreground">Every parsed account is already in UBM. New accounts appear here as fresh bills arrive, or upload a file to add more.</p>
              </Card>
            ) : (
            <Tabs defaultValue="properties">
              <TabsList className="rounded-none">
                <TabsTrigger value="properties" className="rounded-none" data-testid="tab-properties"><Building2 className="h-4 w-4 mr-1" /> Properties</TabsTrigger>
                <TabsTrigger value="vendor_accounts" className="rounded-none" data-testid="tab-vendors"><Network className="h-4 w-4 mr-1" /> Vendor Accounts</TabsTrigger>
                <TabsTrigger value="unit_accounts" className="rounded-none" data-testid="tab-units"><Boxes className="h-4 w-4 mr-1" /> Unit Accounts</TabsTrigger>
                <TabsTrigger value="meters" className="rounded-none" data-testid="tab-meters"><Gauge className="h-4 w-4 mr-1" /> Meters</TabsTrigger>
              </TabsList>

              {['properties', 'vendor_accounts', 'unit_accounts', 'meters'].map((tab) => (
                <TabsContent key={tab} value={tab}>
                  <div className="flex justify-end mb-2">
                    <Button variant="outline" size="sm" className="rounded-none" onClick={() => openAdd(TYPE_OF_TAB[tab])} data-testid={`add-${tab}`}>
                      <Plus className="h-4 w-4 mr-1" /> Add row
                    </Button>
                  </div>
                  <Card className="rounded-none divide-y">
                    {(plan[tab] || []).length === 0 ? <div className="p-6 text-center text-muted-foreground text-sm">No {tab.replace('_', ' ')}.</div> :
                      (plan[tab] || []).map((it) => {
                        const subtitle = tab === 'properties' ? `${it.address || ''}${it.city ? `, ${it.city}` : ''} ${it.state || ''} ${it.zip || ''}`
                          : tab === 'vendor_accounts' ? `Acct ${it.account_number || '—'} · ${it.commodity || '—'} · ${it.site_name || ''}`
                            : tab === 'unit_accounts' ? `Acct ${it.account_number || '—'} · ${it.commodity || '—'} · ${(it.meter_numbers || []).length} meter(s)`
                              : `Acct ${it.account_number || '—'} · ${it.vendor_name || ''}`;
                        return renderRow(tab, it, subtitle);
                      })}
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
            )}
          </>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none" data-testid="edit-drawer">
          <DialogHeader><DialogTitle>{editing?.isNew ? 'Add' : 'Edit'} {editing?.item_type?.replace('_', ' ')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editing && EDITABLE[editing.item_type].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Input className="rounded-none" value={form[k] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`field-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={saveEdit} data-testid="edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
